import { DataTable } from "../DataTable";
import { IconEye, IconEyeOff, IconCopy, IconCheck } from "@tabler/icons-react";
import { ActionIcon, Group, Tooltip, Text } from "@mantine/core";
import {
  TableActions,
  TableAction,
  DeleteAction,
  actionsColumn,
} from "../TableActions";
import { IconEdit } from "@tabler/icons-react";
import { openCreateNewTicketModal } from "./CreateNewTicketModal";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";

import { useState } from "react";
import { useClipboard } from "@mantine/hooks";
import { useDeleteTicket } from "../../api/newTickets";

export function NewTicketsDatatable({
  data,
  pagination,
  fetching,
  setPage,
  page,
}) {
  const [visibleCodes, setVisibleCodes] = useState({});
  const [deleteTicketId, setDeleteTicketId] = useState(null);
  const [copiedRowId, setCopiedRowId] = useState(null);

  const { mutate: deleteTicket } = useDeleteTicket({ id: deleteTicketId });
  const confirmDelete = useDeleteConfirm("ticket", deleteTicket);

  const clipboard = useClipboard({ timeout: 500 });

  const toggleCodeVisibility = (id) => {
    setVisibleCodes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleEditTicket = (ticketData) => {
    openCreateNewTicketModal({ action: "edit", ticketData });
  };

  const handleDeleteTicket = (row) => {
    setDeleteTicketId(row.id);
    confirmDelete({});
  };

  const handleCopyToClipboard = (code, rowId) => {
    clipboard.copy(code);
    setCopiedRowId(rowId);
    setTimeout(() => setCopiedRowId(null), 1000);
  };

  return (
    <DataTable
      records={data?.data}
      totalRecords={pagination?.total}
      recordsPerPage={pagination?.perPage}
      page={page}
      onPageChange={(p) => setPage(p)}
      fetching={fetching}
      pinLastColumn
      columns={[
        { accessor: "firstName" },
        { accessor: "lastName" },
        { accessor: "email" },
        {
          title: "Ticket code",
          accessor: "secretCode",
          width: "0%",
          render: (row) => {
            const isVisible = visibleCodes[row.id];
            const displayCode = isVisible
              ? row.secretCode
              : `••••${row.secretCode.slice(-2)}`;

            return (
              <Group gap="sm" wrap="nowrap">
                <Text
                  size="sm"
                  ff="monospace"
                  fw={500}
                  c="dimmed"
                  style={{ letterSpacing: "0.5px" }}
                >
                  {displayCode}
                </Text>
                <Group gap={4} wrap="nowrap">
                  <Tooltip label={isVisible ? "Hide code" : "Reveal code"}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => toggleCodeVisibility(row.id)}
                    >
                      {isVisible ? <IconEyeOff /> : <IconEye />}
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip
                    label={
                      copiedRowId === row.id ? "Copied!" : "Copy to clipboard"
                    }
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() =>
                        handleCopyToClipboard(row.secretCode, row.id)
                      }
                    >
                      {copiedRowId === row.id ? <IconCheck /> : <IconCopy />}
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
            );
          },
        },
        actionsColumn(
          (row) => (
            <TableActions>
              <TableAction
                label="Edit ticket"
                icon={IconEdit}
                onClick={() => handleEditTicket(row)}
              />
              <DeleteAction
                label="Delete ticket"
                onDelete={() => handleDeleteTicket(row)}
              />
            </TableActions>
          ),
          { title: "Actions" }
        ),
      ]}
    />
  );
}
